// Electricity Plan Modal - Self-contained modal for displaying electricity plan details

import { useEffect } from 'react'
import type { ElectricityPlan } from '@/types'

interface ElectricityPlanModalProps {
  plan: ElectricityPlan
  onClose: () => void
  onSelect: () => void
  isSelected: boolean
}

// Get document display name
function getDocName(doc: { type?: string; name?: string }): string {
  if (doc.name) return doc.name
  if (doc.type === 'efl') return 'Electricity Facts Label (EFL)'
  if (doc.type === 'tos') return 'Terms of Service'
  if (doc.type === 'yraac') return 'Your Rights As A Customer'
  return 'Document'
}

export function ElectricityPlanModal({
  plan,
  onClose,
  onSelect,
  isSelected,
}: ElectricityPlanModalProps) {
  const isFullyRenewable = plan.percentGreen === 100

  // Handle overlay click (close modal)
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  return (
    <div className="modal modal--active">
      <div className="modal__overlay" onClick={handleOverlayClick}></div>
      <div className="modal__container">
        {/* Header */}
        <div className="modal__header">
          <h2 className="modal__title">{plan.name}</h2>
          <button className="modal__close" onClick={onClose} aria-label="Close modal">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="modal__body">
          {/* Provider Logo */}
          {plan.logo && (
            <div className="modal__section">
              <img
                src={plan.logo}
                alt={plan.vendorName}
                className="modal__logo"
                onError={(e) => { e.currentTarget.style.display = 'none' }}
              />
            </div>
          )}

          {/* Plan Highlights */}
          {plan.shortDescription && (
            <div className="modal__section">
              <h3 className="modal__section-title">Plan Highlights</h3>
              <p className="modal__headline">{plan.shortDescription}</p>
            </div>
          )}

          {/* Key Details */}
          <div className="modal__section">
            <h3 className="modal__section-title">Key Details</h3>
            <dl className="modal__details-list">
              <dt>Provider:</dt>
              <dd>{plan.vendorName}</dd>

              <dt>Price per kWh:</dt>
              <dd>{plan.pricePerKwhCents?.toFixed(2)}¢/kWh</dd>

              <dt>Estimated Monthly Cost:</dt>
              <dd className="modal__highlight">${plan.estimatedMonthlyCost?.toFixed(2)}</dd>

              <dt>Contract Term:</dt>
              <dd>{plan.termLength} months</dd>

              <dt>Renewable Energy:</dt>
              <dd>
                {plan.percentGreen}%
                {isFullyRenewable && (
                  <span className="modal__badge modal__badge--green">100% Renewable</span>
                )}
              </dd>

              {plan.earlyTerminationFee != null && plan.earlyTerminationFee > 0 && (
                <>
                  <dt>Early Termination Fee:</dt>
                  <dd>${plan.earlyTerminationFee}</dd>
                </>
              )}
            </dl>
          </div>

          {/* Pricing at Different Usage Levels */}
          <div className="modal__section">
            <h3 className="modal__section-title">Pricing at Different Usage Levels</h3>
            <div className="modal__pricing-grid">
              {[
                { usage: 500, label: '500 kWh/month' },
                { usage: 1000, label: '1,000 kWh/month' },
                { usage: 2000, label: '2,000 kWh/month', highlighted: true },
              ].map(({ usage, label, highlighted }) => {
                const monthlyCost = (plan.pricePerKwhCents || 0) * usage / 100
                return (
                  <div
                    key={usage}
                    className={`modal__pricing-card ${highlighted ? 'modal__pricing-card--highlighted' : ''}`}
                  >
                    <div className="modal__pricing-usage">{label}</div>
                    <div className="modal__pricing-cost">${monthlyCost.toFixed(2)}/mo</div>
                    <div className="modal__pricing-rate">{plan.pricePerKwhCents?.toFixed(2)}¢/kWh</div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Document Links */}
          {plan.documentLinks && plan.documentLinks.length > 0 && (
            <div className="modal__section">
              <h3 className="modal__section-title">Important Documents</h3>
              <ul className="modal__document-list">
                {plan.documentLinks.map((doc, idx) => (
                  <li key={idx}>
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="modal__document-link"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                      </svg>
                      {getDocName(doc)}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Disclaimer */}
          <div className="modal__section modal__section--disclaimer">
            <p className="modal__disclaimer">
              <strong>Important:</strong> Prices and availability are subject to change.
              Please review all terms and conditions before selecting a plan.
              Your actual usage may vary based on home size, weather, and personal habits.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="modal__footer">
          <button
            type="button"
            className={`btn ${isSelected ? 'btn--outline' : ''}`}
            onClick={onSelect}
            style={{ width: '100%' }}
          >
            {isSelected ? 'Selected' : 'Select This Plan'}
          </button>
        </div>
      </div>
    </div>
  )
}
